import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getFileUrl } from '../../lib/api';
import { heroImgClass, heroWrapperClass, heroHeightClass } from '../../lib/heroFit';

export const HeroSliderV2 = ({ slides = [] }) => {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const nextSlide = useCallback(() => {
    if (slides.length > 0) {
      setCurrent(prev => (prev + 1) % slides.length);
    }
  }, [slides.length]);

  const prevSlide = () => {
    if (slides.length > 0) {
      setCurrent(prev => (prev - 1 + slides.length) % slides.length);
    }
  };

  useEffect(() => {
    if (isPaused || slides.length <= 1) return;
    const timer = setInterval(nextSlide, 6000);
    return () => clearInterval(timer);
  }, [isPaused, nextSlide, slides.length]);

  if (slides.length === 0) {
    return (
      <div className="relative w-full h-screen bg-[#0f1419] flex items-center justify-center">
        <p className="text-gray-400 font-heading text-lg">Slider görsel eklenmemiş</p>
      </div>
    );
  }

  const slide = slides[current];
  const imgSrc = getFileUrl(slide.image_path);
  const fit = slide.fit_mode;

  return (
    <div
      className={`relative w-full ${heroHeightClass(fit, 'h-screen', 'md:h-screen')} overflow-hidden bg-[#0f1419]`}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      data-testid="home-hero-carousel-v2"
    >
      {/* Background Image - fit per slider setting (full image on mobile by default) */}
      <div className={heroWrapperClass(fit)}>
        <img
          src={imgSrc}
          alt={slide.title}
          className={heroImgClass(fit)}
          key={current}
        />
        {/* Subtle dark overlay for text readability */}
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Centered Content - only when there is text/CTA (image-only banners stay clean) */}
      {(slide.title || slide.subtitle || slide.link) && (
      <div className="absolute inset-0 z-10 h-full flex flex-col items-center justify-center text-center px-4 sm:px-6">
        <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-3 drop-shadow-lg max-w-4xl">
          {slide.title}
        </h1>
        {slide.subtitle && (
          <p className="text-sm sm:text-lg tracking-[0.2em] uppercase text-white/80 mb-6 drop-shadow max-w-2xl">
            {slide.subtitle}
          </p>
        )}
        {/* Decorative line */}
        <div className="w-16 h-0.5 bg-white/50 mb-6" />
        {slide.link && (
          <Link
            to={slide.link}
            className="inline-flex items-center px-8 py-3 border border-white/50 text-white text-sm font-medium tracking-wider uppercase hover:bg-white hover:text-black transition-all duration-300"
            data-testid="home-hero-primary-cta-v2"
          >
            {slide.button_text || 'Projeyi İncele'}
          </Link>
        )}
      </div>
      )}

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={prevSlide}
            className="absolute left-6 top-1/2 -translate-y-1/2 z-20 p-3 text-white/70 hover:text-white transition-colors"
            data-testid="home-hero-carousel-prev"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-6 top-1/2 -translate-y-1/2 z-20 p-3 text-white/70 hover:text-white transition-colors"
            data-testid="home-hero-carousel-next"
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          {/* Bottom Slide Indicators - Line style */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-3">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`h-0.5 rounded-full transition-all duration-500 ${
                  i === current ? 'w-10 bg-white' : 'w-6 bg-white/40'
                }`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
